import type {
  ClubEventDto,
  ClubGoalDto,
  ClubId,
  ClubPageDto,
  ClubSeasonDto,
  ClubStandingDto,
} from "@clubap/shared";
import { authedJson } from "../api.js";

/**
 * Cliente de `/api/clubs`: la ficha, los eventos, la tabla y la meta.
 *
 * Vive aparte de `social/api.ts` y no dentro, aunque los dos hablen de clubes, porque
 * son dos superficies distintas del servidor: `/api/social/clubs/...` es la LIBRETA —lo
 * que un jugador tiene con un club: entrar, salir, pedir, el tablón— y `/api/clubs/...`
 * es el club como institución: su ficha pública, su calendario, su marcador. Partirlo
 * por el prefijo de la ruta es lo que hace que se vea de un vistazo qué endpoint está
 * tocando cada pantalla; mezclarlos dejaría un archivo donde `fetchClubs` y
 * `fetchClubPage` parecen la misma consulta y no lo son.
 *
 * Lo que YA está en `social/api.ts` no se repite acá: el catálogo, el logo, las
 * solicitudes de fundación y `clubLogoUrl` se importan de allá.
 */

/** La ficha completa. Un solo viaje: roster, eventos, tablón, bitácora y permisos. */
export function fetchClubPage(clubId: ClubId): Promise<ClubPageDto> {
  return authedJson<ClubPageDto>(`/api/clubs/${encodeURIComponent(clubId)}`);
}

/**
 * Los eventos de un club, solos.
 *
 * La ficha ya los trae, así que esto no es para pintar la pantalla la primera vez: es
 * para volver a pedirlos cuando algo los cambió y la respuesta no los devolvió —cancelar
 * contesta `{ ok: true }`— sin tener que recargar la ficha entera y perder el scroll.
 */
export function fetchClubEvents(clubId: ClubId): Promise<ClubEventDto[]> {
  return authedJson<{ events: ClubEventDto[] }>(
    `/api/clubs/${encodeURIComponent(clubId)}/events`,
  ).then((result) => result.events);
}

/** Lo que hay que decir para convocar. `startsAt` es epoch en ms, del reloj del navegador. */
export interface EventDraft {
  title: string;
  roomId: string;
  startsAt: number;
  minutes: number;
}

/**
 * Convocar devuelve el evento Y la lista entera.
 *
 * Las dos cosas, por lo mismo que entrar a un club devuelve el resumen completo: si la
 * pantalla dependiera de un aviso en vivo para enterarse, con el socket caído la petición
 * saldría bien y el calendario se quedaría igual.
 */
export interface EventCreated {
  event: ClubEventDto;
  events: ClubEventDto[];
}

export function createClubEvent(clubId: ClubId, draft: EventDraft): Promise<EventCreated> {
  return authedJson<EventCreated>(`/api/clubs/${encodeURIComponent(clubId)}/events`, draft);
}

/** Cancelar va por el id del EVENTO, no por el del club: un evento sólo está en uno. */
export function cancelClubEvent(eventId: string): Promise<void> {
  return authedJson<{ ok: true }>(
    `/api/clubs/events/${encodeURIComponent(eventId)}/cancel`,
    {},
  ).then(() => undefined);
}

/** La tabla de la temporada abierta, con la temporada a la que pertenece. */
export interface StandingsView {
  standings: ClubStandingDto[];
  season: ClubSeasonDto;
}

export function fetchStandings(): Promise<StandingsView> {
  return authedJson<StandingsView>("/api/clubs/standings");
}

/** El historial de temporadas, para el palmarés. La abierta viene con `closedAt` en null. */
export function fetchSeasons(): Promise<ClubSeasonDto[]> {
  return authedJson<{ seasons: ClubSeasonDto[] }>("/api/clubs/seasons").then(
    (result) => result.seasons,
  );
}

/**
 * Fijar la meta de recaudación. Sólo la puede poner quien modera, y lo comprueba el
 * servidor: acá no se pregunta por papeles, se manda y se enseña lo que conteste.
 */
export function setClubGoal(
  clubId: ClubId,
  goal: { title: string; target: number },
): Promise<ClubGoalDto> {
  return authedJson<{ goal: ClubGoalDto }>(
    `/api/clubs/${encodeURIComponent(clubId)}/goal`,
    goal,
  ).then((result) => result.goal);
}
